import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import RoddoPageShell from './layout/RoddoPageShell';
import { deleteCanonicalCatch, deleteLegacyCatchLogEntry, type CatchTimelineEntry } from '../services/catchDiary';
import { FishIcon } from './icons';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import CatchLocationMapPreview from './CatchLocationMapPreview';
import RoddoPillTabs from './RoddoPillTabs';
import RoddoConfirmDialog from './RoddoConfirmDialog';
import { archiveCommunityPost } from '../services/community';
import { deleteCatchStorageImages } from '../services/imageUpload';

interface CatchHistoryViewProps {
  entries: CatchTimelineEntry[];
  onBack: () => void;
  onLogCatch?: () => void;
  userId: string;
  onEditCatch?: (entry: CatchTimelineEntry) => void;
  addToast?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
  viewState?: {
    targetId?: string | null;
    source?: 'log' | 'diary' | 'all' | string;
    species?: string | null;
  } | null;
}

type SourceFilter = 'all' | 'diary' | 'log';
type SortMode = 'newest' | 'oldest' | 'species' | 'location';

const CatchHistoryView: React.FC<CatchHistoryViewProps> = ({
  entries,
  onBack,
  onLogCatch,
  userId,
  onEditCatch,
  addToast,
  viewState,
}) => {
  const { t } = useTranslation();

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveRoddoDarkMode());

  useEffect(() => {
    let lastTheme = resolveRoddoDarkMode();

    const syncTheme = () => {
      const nextTheme = resolveRoddoDarkMode();
      if (nextTheme !== lastTheme) {
        lastTheme = nextTheme;
        setIsDarkMode(nextTheme);
      }
    };

    setIsDarkMode(lastTheme);

    const media =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            window.requestAnimationFrame(syncTheme);
          })
        : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
      if (document.body) {
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-theme', 'style'],
        });
      }
    }

    const intervalId = window.setInterval(syncTheme, 220);

    window.addEventListener('rodo-theme-change', syncTheme as EventListener);
    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);
    window.addEventListener('focus', syncTheme as EventListener);
    window.addEventListener('pageshow', syncTheme as EventListener);
    window.addEventListener('visibilitychange', syncTheme as EventListener);
    media?.addEventListener?.('change', syncTheme);

    return () => {
      observer?.disconnect();
      window.clearInterval(intervalId);
      window.removeEventListener('rodo-theme-change', syncTheme as EventListener);
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
      window.removeEventListener('focus', syncTheme as EventListener);
      window.removeEventListener('pageshow', syncTheme as EventListener);
      window.removeEventListener('visibilitychange', syncTheme as EventListener);
      media?.removeEventListener?.('change', syncTheme);
    };
  }, []);

  const ui = getRoddoUiTokens(isDarkMode);

  const logTextPrimary = isDarkMode ? '#F4F6FA' : ui.textPrimary;
  const logTextSecondary = isDarkMode ? '#AEB8C9' : ui.textSecondary;
  const logTextMuted = isDarkMode ? '#7F8BA0' : ui.textMuted;

  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [deleteTarget, setDeleteTarget] = useState<CatchTimelineEntry | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionMenuEntryId, setActionMenuEntryId] = useState<string | null>(null);

  useEffect(() => {
    const species = String(viewState?.species || '').trim();
    if (!species) return;

    setSourceFilter('all');
    setSortMode('newest');
    setSearchQuery(species);
    setOpenEntryId(null);
  }, [viewState?.species]);

  const topCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : (ui.cardBgStrong ?? ui.cardBg),
    border: `1px solid ${isDarkMode ? 'rgba(132,151,190,0.17)' : (ui.borderStrong ?? ui.border)}`,
    boxShadow: isDarkMode ? '0 6px 16px rgba(0,0,0,0.14)' : `${ui.shadowStrong ?? ui.shadow}, ${ui.insetHighlight}`,
  };

  const chipStyle: React.CSSProperties = {
    background: isDarkMode
      ? '#0C1423'
      : ui.cardBgMuted,
    border: `1px solid ${isDarkMode ? 'rgba(132,151,190,0.17)' : ui.border}`,
    color: logTextPrimary,
    boxShadow: isDarkMode
      ? '0 5px 14px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.03)'
      : `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.16)`,
  };

  const cardWrapStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : (ui.cardBgStrong ?? ui.cardBg),
    border: `1px solid ${isDarkMode ? 'rgba(132,151,190,0.17)' : (ui.borderStrong ?? ui.border)}`,
    boxShadow: isDarkMode ? '0 5px 14px rgba(0,0,0,0.13)' : `${ui.shadowStrong ?? ui.shadow}, ${ui.insetHighlight}`,
  };

  const innerCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#0C1423' : ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? 'none' : `${ui.shadow}, ${ui.insetHighlight}`,
  };

  const kvStyle: React.CSSProperties = {
    background: ui.cardBgMuted,
    border: `1px solid ${ui.border}`,
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12)`,
  };

  const gearSectionStyle: React.CSSProperties = {
    background: isDarkMode ? '#0F1828' : (ui.cardBgStrong ?? ui.cardBg),
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? 'none' : `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.14)`,
  };

  const gearTileStyle: React.CSSProperties = {
    background: ui.cardBgMuted,
    border: `1px solid ${ui.border}`,
    boxShadow: `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.12)`,
  };

  const pillClass = 'inline-flex items-center gap-2 rounded-[12px] px-3 py-1 text-[11px] font-extrabold transition-transform hover:-translate-y-0.5';

  const sourcePill = (source: CatchTimelineEntry['source']) => {
    if (source === 'log') {
      return (
        <span
          className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-extrabold"
          style={{
            background: isDarkMode ? 'rgba(251,113,133,0.20)' : 'rgba(249,115,115,0.90)',
            color: isDarkMode ? '#FFD7DF' : '#111827',
            border: `1px solid ${isDarkMode ? 'rgba(251,113,133,0.34)' : 'rgba(249,115,115,0.40)'}`,
          }}
        >
          {t('catchHistory.source.log', 'Log entry')}
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-extrabold"
        style={{
          background: isDarkMode ? 'rgba(199,207,248,0.16)' : 'rgba(210,219,242,0.90)',
          color: isDarkMode ? '#F3F1FF' : '#2d2b42',
          border: `1px solid ${isDarkMode ? 'rgba(214,207,255,0.22)' : 'rgba(155,121,43,0.35)'}`,
        }}
      >
        {t('catchHistory.source.diary', 'Diary')}
      </span>
    );
  };

  const totalCatches = entries.length;

  const formatDate = (entry: CatchTimelineEntry): string => {
    if (!entry.createdAt) return '';
    try {
      return entry.createdAt.toLocaleString();
    } catch {
      return '';
    }
  };

  const formatTimeOfDay = (entry: CatchTimelineEntry): string => {
    const d = entry.createdAt instanceof Date ? entry.createdAt : null;
    if (!d) return t('catchHistory.timeOfDay.unknown', 'Unknown time');
    const h = d.getHours();
    if (h >= 4 && h < 8) return t('catchHistory.timeOfDay.dawn', 'Dawn');
    if (h >= 8 && h < 12) return t('catchHistory.timeOfDay.morning', 'Morning');
    if (h >= 12 && h < 16) return t('catchHistory.timeOfDay.midday', 'Midday');
    if (h >= 16 && h < 20) return t('catchHistory.timeOfDay.evening', 'Evening');
    return t('catchHistory.timeOfDay.night', 'Night');
  };

  const safeSpecies = (entry: CatchTimelineEntry): string =>
    entry.speciesName || entry.notes || t('catchHistory.untitled', 'Logged catch');

  const safeLocation = (entry: CatchTimelineEntry): string =>
    entry.locationName || t('catchHistory.unknownLocation', 'Unknown spot');

  const getCatchCoords = (raw: any): { lat: number; lon: number } | null => {
    const lat = Number(raw?.coords?.lat ?? raw?.lat ?? raw?.latitude);
    const lon = Number(
      raw?.coords?.lon ??
      raw?.coords?.lng ??
      raw?.lon ??
      raw?.lng ??
      raw?.longitude,
    );

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { lat, lon };
  };

  const filteredEntries = useMemo(() => {
    const queryText = searchQuery.trim().toLowerCase();

    const rows = entries.filter((entry) => {
      if (sourceFilter !== 'all' && (entry.source || 'diary') !== sourceFilter) return false;
      if (!queryText) return true;

      const raw: any = entry.raw || {};
      const haystack = [
        entry.speciesName,
        entry.locationName,
        entry.notes,
        raw.species,
        raw.location,
        raw.waterName,
        raw.weight,
        raw.rod,
        raw.reel,
        raw.lureOrBait,
        raw.lure,
        raw.bait,
        raw.technique,
        raw.notes,
        raw.waterCondition,
        raw.conditions,
        raw.date,
        raw.time,
        entry.createdAt?.toLocaleDateString?.(),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });

    return [...rows].sort((a, b) => {
      if (sortMode === 'oldest') return a.createdAt.getTime() - b.createdAt.getTime();
      if (sortMode === 'species') return safeSpecies(a).localeCompare(safeSpecies(b));
      if (sortMode === 'location') return safeLocation(a).localeCompare(safeLocation(b));
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }, [entries, sourceFilter, searchQuery, sortMode]);

  const prioritizedEntries = useMemo(() => {
    const targetId = String(viewState?.targetId || '').trim();
    if (!targetId) return filteredEntries;

    const targetIndex = filteredEntries.findIndex((entry) => String(entry.id || '').trim() === targetId);
    if (targetIndex <= 0) return filteredEntries;

    const next = [...filteredEntries];
    const [targetEntry] = next.splice(targetIndex, 1);
    next.unshift(targetEntry);
    return next;
  }, [filteredEntries, viewState?.targetId]);

  useEffect(() => {
    const targetId = String(viewState?.targetId || '').trim();
    if (!targetId) return;

    const targetEntry = entries.find((entry) => String(entry.id || '').trim() === targetId);
    if (!targetEntry) return;

    const nextSource = String(viewState?.source || '').trim().toLowerCase();
    if (nextSource === 'log' || nextSource === 'diary' || nextSource === 'all') {
      setSourceFilter(nextSource as SourceFilter);
    }

    setOpenEntryId(targetId);

    const scrollTimer = window.setTimeout(() => {
      const node = document.getElementById(`catch-history-entry-${targetId}`);
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);

    return () => window.clearTimeout(scrollTimer);
  }, [entries, viewState?.source, viewState?.targetId]);

  const toggleOpen = (id: string) => {
    setActionMenuEntryId(null);
    setOpenEntryId((prev) => (prev === id ? null : id));
  };

  const renderKV = (label: string, value?: any) => {
    const v = typeof value === 'string' ? value.trim() : value;
    if (v === null || v === undefined || v === '') return null;
    return (
      <div className="rounded-[14px] px-3 py-2" style={kvStyle}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
          {label}
        </p>
        <p className="mt-1 break-words text-[12px] font-semibold" style={{ color: logTextPrimary }}>
          {String(v)}
        </p>
      </div>
    );
  };

  const getGearSlots = (raw: any) => {
    const trim = (value: any): string => (typeof value === 'string' ? value.trim() : '');
    const first = (...values: any[]): string => {
      for (const value of values) {
        const next = trim(value);
        if (next) return next;
      }
      return '';
    };

    const lureType = first(raw?.lureOrBaitType, raw?.equipment?.lureOrBaitType);
    const lureLabel = lureType === 'Bait'
      ? t('catchHistory.gear.bait', 'Bait')
      : t('catchHistory.gear.lureOrBait', 'Lure / bait');

    return [
      {
        key: 'rod',
        label: t('catchHistory.gear.rod', 'Rod'),
        value: first(raw?.rod, raw?.rodName, raw?.equipment?.rod, raw?.equipment?.rodName),
      },
      {
        key: 'reel',
        label: t('catchHistory.gear.reel', 'Reel'),
        value: first(raw?.reel, raw?.reelName, raw?.equipment?.reel, raw?.equipment?.reelName),
      },
      {
        key: 'lureOrBait',
        label: lureLabel,
        value: first(
          raw?.lureOrBait,
          raw?.lureOrBaitName,
          raw?.lure,
          raw?.lureName,
          raw?.bait,
          raw?.baitName,
          raw?.equipment?.lureOrBait,
          raw?.equipment?.lureOrBaitName,
          raw?.equipment?.lure,
          raw?.equipment?.lureName,
          raw?.equipment?.bait,
          raw?.equipment?.baitName,
        ),
      },
      {
        key: 'lureType',
        label: t('catchHistory.gear.type', 'Type'),
        value: lureType,
      },
      {
        key: 'technique',
        label: t('catchHistory.gear.technique', 'Technique'),
        value: first(raw?.technique, raw?.equipment?.technique),
      },
      {
        key: 'line',
        label: t('catchHistory.gear.line', 'Line'),
        value: first(raw?.line, raw?.lineName, raw?.equipment?.line, raw?.equipment?.lineName),
      },
      {
        key: 'hook',
        label: t('catchHistory.gear.hook', 'Hook'),
        value: first(raw?.hook, raw?.hookName, raw?.equipment?.hook, raw?.equipment?.hookName),
      },
      {
        key: 'smart',
        label: t('catchHistory.gear.smart', 'Smart'),
        value: first(raw?.smartDevice, raw?.smartGear, raw?.equipment?.smartDevice, raw?.equipment?.smartGear),
      },
    ].filter((slot) => !!slot.value);
  };

  const renderGearSection = (raw: any) => {
    const gearSlots = getGearSlots(raw);
    const summary = typeof raw?.equipmentSummary === 'string' ? raw.equipmentSummary.trim() : '';

    if (!gearSlots.length && !summary) return null;

    return (
      <div className="mt-3 rounded-[20px] p-3" style={gearSectionStyle}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
              {t('catchHistory.gear.label', 'Gear used')}
            </p>
            <p className="mt-1 text-[12px] font-extrabold" style={{ color: logTextPrimary }}>
              {t('catchHistory.gear.subtitle', 'Equipped setup from this catch')}
            </p>
          </div>
          <div className="rounded-full px-2.5 py-1 text-[10px] font-extrabold" style={{ ...gearTileStyle, color: logTextPrimary }}>
            {gearSlots.length > 0 ? `${gearSlots.length} ${gearSlots.length === 1 ? 'item' : 'items'}` : 'Summary'}
          </div>
        </div>

        {summary ? (
          <div className="mt-3 rounded-[14px] px-3 py-2" style={gearTileStyle}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
              {t('catchHistory.gear.summary', 'Setup summary')}
            </p>
            <p className="mt-1 break-words text-[12px] font-semibold leading-5" style={{ color: logTextPrimary }}>
              {summary}
            </p>
          </div>
        ) : null}

        {gearSlots.length > 0 ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {gearSlots.map((slot) => (
              <div key={slot.key} className="rounded-[14px] px-3 py-2.5" style={gearTileStyle}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
                  {slot.label}
                </p>
                <p className="mt-1 break-words text-[12px] font-semibold leading-5" style={{ color: logTextPrimary }}>
                  {slot.value}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    if (!lightboxUrl) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxUrl(null);
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxUrl]);

  useEffect(() => {
    if (!actionMenuEntryId) return;

    const closeMenu = () => setActionMenuEntryId(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('keydown', onKey);
    };
  }, [actionMenuEntryId]);

  const requestDelete = (entry: CatchTimelineEntry) => {
    const raw: any = entry.raw || {};
    if (raw.tournamentEntry || raw.tournamentId || raw.tournamentSubmissionId) {
      addToast?.('Tournament catches are locked to preserve submitted evidence.', 'info');
      return;
    }
    setDeleteTarget(entry);
  };

  const performDelete = async (deleteCommunityPost: boolean) => {
    if (!deleteTarget || deleteBusy) return;
    const raw: any = deleteTarget.raw || {};

    setDeleteBusy(true);
    try {
      let mediaCleanupFailed = false;

      if (deleteTarget.source === 'log') {
        await deleteLegacyCatchLogEntry({ userId, catchId: deleteTarget.id });
      } else {
        const result = await deleteCanonicalCatch({ userId, catchId: deleteTarget.id });
        mediaCleanupFailed = result.mediaCleanupFailed;
      }

      let communityArchiveFailed = false;
      if (deleteTarget.source === 'diary' && deleteCommunityPost && raw.communityPostId) {
        try {
          await archiveCommunityPost(String(raw.communityPostId));

          const urls = Array.from(
            new Set(
              [
                ...(Array.isArray(raw?.images) ? raw.images : []),
                ...(Array.isArray(raw?.imageUrls) ? raw.imageUrls : []),
                raw?.imageUrl,
                raw?.image,
                raw?.photoUrl,
              ]
                .map((value) => String(value || '').trim())
                .filter(Boolean),
            ),
          );

          const cleanup = await deleteCatchStorageImages({
            uid: userId,
            catchId: deleteTarget.id,
            urls,
            includeCommunity: true,
          });
          mediaCleanupFailed = mediaCleanupFailed || cleanup.failed > 0;
        } catch (postError) {
          communityArchiveFailed = true;
          console.error('[CatchHistory] catch deleted but Baleen Bay archive failed', postError);
          addToast?.('Catch deleted, but Roddo could not remove the Baleen Bay post. Try deleting that post from Baleen Bay.', 'warning');
        }
      }

      setOpenEntryId((prev) => (prev === deleteTarget.id ? null : prev));
      setDeleteTarget(null);

      if (!communityArchiveFailed) {
        if (mediaCleanupFailed) {
          addToast?.(
            deleteCommunityPost
              ? 'Catch and Baleen Bay post deleted. One or more old image files may still need cleanup.'
              : 'Catch deleted from Roddo Log. One or more old image files may still need cleanup.',
            'warning',
          );
        } else {
          addToast?.(deleteCommunityPost ? 'Catch and Baleen Bay post deleted.' : 'Catch deleted from Roddo Log.', 'success');
        }
      }
    } catch (error: any) {
      console.error('[CatchHistory] delete failed', error);
      addToast?.(error?.message || 'Roddo could not delete that catch.', 'error');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <RoddoPageShell padding="sm" contentClassName="max-w-4xl roddo-log-lane">
      <div className="space-y-3 pb-24 roddo-log-polish" data-roddo-polish-lane="2" data-roddo-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="sticky top-0 z-30">
  <div className="relative flex items-center justify-center">
    <div className="w-full max-w-[280px]">
      <RoddoPillTabs
        items={[
          { key: 'log', label: t('logCatch.tabs.log', 'Log Catch') },
          { key: 'history', label: t('logCatch.tabs.history', 'History') },
        ]}
        activeKey="history"
        onChange={(key) => {
          if (key === 'log') {
            if (onLogCatch) onLogCatch();
            else onBack();
          }
        }}
        ariaLabel={t('logCatch.tabs.label', 'Roddo Log') as string}
      />
    </div>

    <button
      type="button"
      onClick={onBack}
      aria-label="Go back"
      title="Back"
      className="absolute right-0 grid h-11 w-11 place-items-center rounded-full border text-[22px] font-black leading-none transition hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
      style={{
        background: isDarkMode ? '#101827' : ui.cardBg,
        borderColor: isDarkMode
          ? 'rgba(132,151,190,0.22)'
          : ui.borderStrong,
        color: logTextPrimary,
        boxShadow: isDarkMode
          ? '0 6px 16px rgba(0,0,0,0.18)'
          : `${ui.shadow}, ${ui.insetHighlight}`,
      }}
    >
      ‹
    </button>
  </div>
</div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: logTextMuted }}>
            {t('catchHistory.label', 'Catch diary')}
          </p>
          <h1 className="text-xl font-extrabold tracking-tight" style={{ color: logTextPrimary }}>
            {t('catchHistory.title', 'Your catch history')}
          </h1>
          <p className="mt-1 max-w-md text-xs" style={{ color: logTextSecondary }}>
            {t(
              'catchHistory.subtitle',
              'A unified timeline of every catch you log or add from Community, ready for FishIQ.',
            )}
          </p>
        </div>

        <div className="rounded-[20px] px-4 py-3 sm:px-5 sm:py-4" style={topCardStyle}>
          <div className="relative z-10 flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: logTextMuted }}>
                {t('catchHistory.totalCatches', 'Total catches')}
              </p>
              <p className="mt-1 text-2xl font-extrabold" style={{ color: logTextPrimary }}>
                {totalCatches}
              </p>
            </div>

            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: logTextMuted }}>
                {t('catchHistory.timelineLabel', 'Timeline')}
              </p>
              <p className="mt-1 text-xs font-semibold" style={{ color: logTextSecondary }}>
                {t('catchHistory.timelineOrder', 'Newest to oldest')}
              </p>
            </div>
          </div>
        </div>


        <div className="rounded-[20px] px-4 py-3 sm:px-5 sm:py-4" style={topCardStyle}>
          <div className="grid gap-3">
            {String(viewState?.species || '').trim() ? (
              <div className="flex items-center justify-between gap-3 rounded-[14px] border px-3 py-2" style={{ background: isDarkMode ? 'rgba(102,214,169,0.08)' : 'rgba(234,255,245,0.78)', borderColor: isDarkMode ? 'rgba(102,214,169,0.16)' : 'rgba(117,201,164,0.26)' }}>
                <div className="min-w-0">
                  <p className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: isDarkMode ? '#78E0BD' : '#28795D' }}>FishIQ species history</p>
                  <p className="mt-0.5 truncate text-[12px] font-extrabold" style={{ color: logTextPrimary }}>{String(viewState?.species || '').trim()}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-black"
                  style={{ borderColor: ui.borderStrong, color: logTextSecondary, background: isDarkMode ? '#0C1423' : ui.cardBg }}
                >
                  Show all
                </button>
              </div>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <label className="relative block">
                <span className="sr-only">Search catch history</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search species, water, lure, gear, notes…"
                  className="w-full rounded-[14px] border px-3.5 py-2.5 pr-9 text-sm font-semibold outline-none"
                  style={{ background: isDarkMode ? '#0C1423' : ui.cardBg, borderColor: ui.borderStrong, color: logTextPrimary }}
                />
                {searchQuery ? (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-xs font-black" style={{ color: logTextMuted }}>×</button>
                ) : null}
              </label>

              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="rounded-[14px] border px-3 py-2.5 text-sm font-extrabold outline-none"
                style={{ background: isDarkMode ? '#0C1423' : ui.cardBg, borderColor: ui.borderStrong, color: logTextPrimary }}
                aria-label="Sort catch history"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="species">Species A–Z</option>
                <option value="location">Location A–Z</option>
              </select>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {(['all', 'diary', 'log'] as SourceFilter[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={pillClass}
                    style={{
                      ...chipStyle,
                      background: sourceFilter === key
                        ? (isDarkMode ? 'rgba(138,112,242,0.18)' : ui.cardBg)
                        : chipStyle.background,
                    }}
                    onClick={() => setSourceFilter(key)}
                  >
                    {key === 'all' ? 'All' : key === 'diary' ? 'Diary' : 'Legacy'}
                  </button>
                ))}
              </div>

              <p className="text-[11px]" style={{ color: logTextSecondary }}>
                Showing <span className="font-extrabold" style={{ color: logTextPrimary }}>{filteredEntries.length}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-[120px] space-y-3">
          {prioritizedEntries.length === 0 && (
            <div className="rounded-[1.5rem] border-2 px-4 py-5" style={{ background: ui.cardBgMuted, borderColor: ui.border, color: logTextSecondary, boxShadow: ui.shadow }}>
              <div className="flex items-start gap-3">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px] border-2 text-2xl" style={{ background: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(255,224,138,0.70)', borderColor: ui.border }}>📓</div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-black" style={{ color: logTextPrimary }}>
                    {t('catchHistory.emptyTitle', 'Capt’n’s Log starts with one honest note.')}
                  </p>
                  <p className="mt-1 text-sm font-semibold leading-relaxed">
                    {t('catchHistory.empty', 'No catches yet. Log a catch, skunk, water clarity, bait choice, or quick observation so FishIQ has real trip context later.')}
                  </p>
                </div>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-4">
                {[
                  ['🌊', t('catchHistory.emptyStepScout', 'Scout Big Blue')],
                  ['🧠', t('catchHistory.emptyStepFishIQ', 'Read FishIQ')],
                  ['🎣', t('catchHistory.emptyStepFish', 'Fish the plan')],
                  ['📓', t('catchHistory.emptyStepLog', 'Log what happened')],
                ].map(([icon, label]) => (
                  <div key={label} className="rounded-[14px] border px-3 py-2 text-xs font-black" style={{ background: ui.cardBg, borderColor: ui.border, color: logTextPrimary }}>
                    <span className="mr-1">{icon}</span>{label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {prioritizedEntries.map((entry) => {
            const created = formatDate(entry);
            const species = safeSpecies(entry);
            const location = safeLocation(entry);
            const photoUrl = entry.photoUrl || null;
            const isOpen = openEntryId === entry.id;

            const rawEntry = ((entry as any)?.raw ?? {}) as any;
            const detailEntry = { ...rawEntry, ...entry } as any;
            const isTournamentCatch = Boolean(rawEntry.tournamentEntry || rawEntry.tournamentId || rawEntry.tournamentSubmissionId);
            const isCommunityImport = rawEntry.createdFrom === 'community' || rawEntry.source === 'community_post';

            const catchCoords = getCatchCoords(rawEntry) || getCatchCoords(detailEntry);

            const imageUrls: string[] = Array.from(
              new Set(
                [
                  photoUrl,
                  ...(Array.isArray(rawEntry.imageUrls) ? rawEntry.imageUrls : []),
                  ...(Array.isArray(rawEntry.images) ? rawEntry.images : []),
                  ...(Array.isArray(detailEntry.imageUrls) ? detailEntry.imageUrls : []),
                  ...(Array.isArray(detailEntry.images) ? detailEntry.images : []),
                ].filter(Boolean),
              ),
            );

            return (
              <div
                key={entry.id}
                id={`catch-history-entry-${entry.id}`}
                className="overflow-hidden rounded-[20px]"
                style={cardWrapStyle}
              >
                <button
                  type="button"
                  onClick={() => toggleOpen(entry.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-stretch gap-3 px-3 py-3 text-left transition-transform hover:-translate-y-[1px] active:translate-y-0"
                >
                  <div className="flex flex-col items-center justify-center">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={species}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxUrl(photoUrl);
                        }}
                        className="h-14 w-14 cursor-zoom-in rounded-[14px] object-cover"
                        style={{ border: `1px solid ${ui.border}` }}
                      />
                    ) : (
                      <div
                        className="flex h-14 w-14 flex-col items-center justify-center rounded-[14px] text-[10px] uppercase tracking-wide"
                        style={{ background: ui.cardBgMuted, color: logTextMuted }}
                      >
                        <FishIcon className="mb-1 h-4 w-4" />
                        <span>{t('catchHistory.noPhoto', 'No photo')}</span>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold" style={{ color: logTextPrimary }}>{species}</p>
                        <p className="mt-[2px] flex items-center gap-1 text-[11px]" style={{ color: logTextSecondary }}>
                          <span className="truncate">{location}</span>
                          {created && (
                            <>
                              <span className="mx-1 opacity-60">•</span>
                              <span>{created}</span>
                            </>
                          )}
                        </p>
                      </div>

                      <div className="hidden items-center gap-2 sm:flex">
                        {sourcePill(entry.source)}
                        <span className="text-[11px] font-semibold" style={{ color: logTextSecondary }}>
                          {isOpen ? t('catchHistory.collapse', 'Hide') : t('catchHistory.expand', 'Details')}
                        </span>
                      </div>
                    </div>

                    {entry.notes && (
                      <p className={`mt-1 text-[11px] ${isOpen ? '' : 'line-clamp-2'}`} style={{ color: logTextSecondary }}>
                        {entry.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end justify-between sm:hidden">
                    {sourcePill(entry.source)}
                    <span className="mt-2 text-[10px] font-semibold" style={{ color: logTextSecondary }}>
                      {isOpen ? t('catchHistory.collapse', 'Hide') : t('catchHistory.expand', 'Details')}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3">
                    <div className="rounded-[20px] p-3" style={innerCardStyle}>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
                          {isTournamentCatch ? 'Tournament evidence · locked' : isCommunityImport ? 'Saved Baleen Bay catch' : entry.source === 'diary' ? 'Editable Roddo Log' : 'Legacy catch entry'}
                        </div>
                        {!isTournamentCatch ? (
                          <div className="relative">
                            <button
                              type="button"
                              aria-label="Catch actions"
                              aria-haspopup="menu"
                              aria-expanded={actionMenuEntryId === entry.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                setActionMenuEntryId((current) => current === entry.id ? null : entry.id);
                              }}
                              className="grid h-9 w-9 place-items-center rounded-[12px] border text-[18px] font-black leading-none transition hover:-translate-y-0.5 active:translate-y-0"
                              style={{
                                background: isDarkMode ? '#111A2A' : '#FFF9F1',
                                borderColor: ui.borderStrong,
                                color: logTextSecondary,
                                boxShadow: isDarkMode ? '0 6px 16px rgba(0,0,0,0.18)' : '0 5px 14px rgba(89,65,37,0.08)',
                              }}
                            >
                              ⋯
                            </button>

                            {actionMenuEntryId === entry.id ? (
                              <div
                                role="menu"
                                className="absolute right-0 top-11 z-50 min-w-[172px] overflow-hidden rounded-[14px] border p-1.5"
                                style={{
                                  background: isDarkMode ? '#0D1524' : '#FFF9F1',
                                  borderColor: ui.borderStrong,
                                  boxShadow: isDarkMode ? '0 16px 34px rgba(0,0,0,0.34)' : '0 14px 30px rgba(89,65,37,0.16)',
                                }}
                                onClick={(event) => event.stopPropagation()}
                              >
                                {entry.source === 'diary' && !isCommunityImport ? (
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                      setActionMenuEntryId(null);
                                      onEditCatch?.(entry);
                                    }}
                                    className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2.5 text-left text-[12px] font-black transition hover:bg-black/[.04] dark:hover:bg-white/[.05]"
                                    style={{ color: isDarkMode ? '#8FC2FF' : '#245F9F' }}
                                  >
                                    <span aria-hidden="true">✎</span>
                                    <span>Edit catch</span>
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setActionMenuEntryId(null);
                                    requestDelete(entry);
                                  }}
                                  className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2.5 text-left text-[12px] font-black transition hover:bg-black/[.04] dark:hover:bg-white/[.05]"
                                  style={{ color: isDarkMode ? '#EF8B91' : '#C83F3F' }}
                                >
                                  <span aria-hidden="true">⌫</span>
                                  <span>{isCommunityImport ? 'Remove from diary' : 'Delete catch'}</span>
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {renderKV(t('catchHistory.details.timeOfDay', 'Time of day'), formatTimeOfDay(entry))}
                        {renderKV(t('catchHistory.details.location', 'Location'), detailEntry.locationName || detailEntry.location)}
                        {renderKV(t('catchHistory.details.species', 'Species'), detailEntry.speciesName || detailEntry.species)}
                        {renderKV(t('catchHistory.details.technique', 'Technique'), detailEntry.technique)}
                        {renderKV(t('catchHistory.details.weather', 'Weather'), detailEntry.weather)}
                        {renderKV(t('catchHistory.details.weight', 'Weight'), detailEntry.weight)}
                        {renderKV(t('catchHistory.details.notes', 'Notes'), detailEntry.notes || entry.notes)}
                      </div>

                      {catchCoords ? (
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
                            {t('catchHistory.details.catchSpot', 'Catch spot')}
                          </p>
                          <CatchLocationMapPreview
                            lat={catchCoords.lat}
                            lon={catchCoords.lon}
                            label={detailEntry.locationName || detailEntry.location || location}
                            isDarkMode={isDarkMode}
                            expandable
                            actionLabel="View larger"
                          />
                        </div>
                      ) : null}

                      {renderGearSection(detailEntry)}

                      {imageUrls.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: logTextMuted }}>
                            {t('catchHistory.details.photos', 'Photos')}
                          </p>
                          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                            {imageUrls.slice(0, 12).map((url, idx) => (
                              <img
                                key={`${url}_${idx}`}
                                src={url}
                                alt={`${species} ${idx + 1}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLightboxUrl(url);
                                }}
                                className="h-16 w-16 flex-shrink-0 cursor-zoom-in rounded-[14px] object-cover"
                                style={{ border: `1px solid ${ui.border}` }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <RoddoConfirmDialog
          open={!!deleteTarget}
          title="Delete this catch?"
          description={
            deleteTarget && ((deleteTarget.raw as any)?.createdFrom === 'community' || (deleteTarget.raw as any)?.source === 'community_post')
              ? 'This removes the saved catch from your private diary. It does not change the original Baleen Bay post.'
              : deleteTarget && deleteTarget.source === 'diary' && (deleteTarget.raw as any)?.communityPostId
                ? 'This catch was shared to Baleen Bay. Choose whether the public post should stay up.'
                : 'This removes the catch from your Roddo Log. This cannot be undone.'
          }
          cancelLabel="Cancel"
          tone="warning"
          icon="🗑️"
          isDarkMode={isDarkMode}
          isBusy={deleteBusy}
          {...(deleteTarget && deleteTarget.source === 'diary' && (deleteTarget.raw as any)?.communityPostId && !((deleteTarget.raw as any)?.createdFrom === 'community' || (deleteTarget.raw as any)?.source === 'community_post')
            ? {
                actions: [
                  {
                    label: deleteBusy ? 'Deleting…' : 'Delete log only',
                    description: 'Keep the Baleen Bay post',
                    tone: 'warning' as const,
                    onClick: () => void performDelete(false),
                    disabled: deleteBusy,
                  },
                  {
                    label: deleteBusy ? 'Deleting…' : 'Delete + Bay post',
                    description: 'Remove both copies',
                    tone: 'warning' as const,
                    onClick: () => void performDelete(true),
                    disabled: deleteBusy,
                  },
                ],
              }
            : {
                confirmLabel: deleteBusy ? 'Deleting…' : 'Delete catch',
                onConfirm: () => void performDelete(false),
              })}
          onCancel={() => {
            if (!deleteBusy) setDeleteTarget(null);
          }}
        />

        {lightboxUrl &&
          ReactDOM.createPortal(
            <div
              onClick={() => setLightboxUrl(null)}
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            >
              <img
                src={lightboxUrl}
                alt="Catch photo"
                className="max-h-[90vh] max-w-[90vw] rounded-[20px] object-contain shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
              />
            </div>,
            document.body,
          )}
      </div>
    </RoddoPageShell>
  );
};

export default CatchHistoryView;
